import React, { useState } from 'react'
import { CustomDialog } from '../../../components/StyledComponents'
import { Checkbox, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormGroup, Radio, RadioGroup } from '@mui/material'
import DefaultTooltip from '../../../components/DefaultTooltip'
import { ControlpanelPrimaryButton, ControlpanelSecondaryButton, GrayIconButton } from '../../../components/StyledButton'
import CloseIcon from '@mui/icons-material/Close';
import PrimaryDivider from '../../../components/PrimaryDivider'
import QuestionIcon16pxSvg from '../../../assets/images/icons/QuestionIcon16pxSvg'
import CustomInput from './../../../components/CustomInput';
import { useTranslation } from 'react-i18next'
import InfoIconYellow16pxSvg from '../../../assets/images/icons/InfoIconYellow16pxSvg'
import CustomSelect from '../../../components/CustomSelect'
import AddIcon from '@mui/icons-material/Add';
import { StyledLinkBlue } from '../../../components/StyledLink'
import ExternalExecutetable from './ExternalExecutetable'
import SaveIcon14pxSvg from './../../../assets/images/icons/SaveIcon14pxSvg';

const EditDeviceDialog = (props) => {
    const { t } = useTranslation()

    const handleCloseDialog = () => {
        props.handleClose()
    }

    const [validationMessages, setValidationMessages] = useState({
        deviceCode: "",
        description: ""
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    }

    const handleCheckedChange = (e) => {
        const { name, checked } = e.target;
        setFormData((prevData) => {
            if (name in prevData.events) {
                return {
                    ...prevData,
                    events: {
                        ...prevData.events,
                        [name]: checked,
                    }
                };
            } else {
                return {
                    ...prevData,
                    [name]: checked,
                };
            }
        });
    };

    const initialFormData = {
        deviceCode: "",
        description: "",
        biometricAuthentication: "identification",
        verifyOnDuplicateMatching: false,
        verificationOrder: "idFirst",
        events: {
            checkIn: true,
            checkOut: true,
            access: true
        },
        externalExecutables: [],
    };

    const [formData, setFormData] = useState(initialFormData)

    const biometricAuthenticationData = [
        { value: "identification", label: "Identification" },
        { value: "verification", label: "Verification" },
        { value: "none", label: "None" }
    ]

    const handleAddExternalExecutables = () => {
        setFormData(prevFormData => ({
            ...prevFormData,
            externalExecutables: [...prevFormData.externalExecutables, {}] // Add a new break time entry
        }));
    };

    const handleRemoveExternalExecutables = (index) => {
        setFormData(prevFormData => ({
            ...prevFormData,
            externalExecutables: prevFormData.externalExecutables.filter((_, i) => i !== index)
        }));
    };

    return (
        <CustomDialog
            maxWidth={"xl"}
            open={props.open}
            onClose={handleCloseDialog}
            keepMounted
        >
            <DialogTitle>
                <div className='flex justify-between'>
                    <div className='flex items-center gap-4'>
                        <p className='text-xl font-semibold '>{t('common.edit-device')} {props.info.deviceCode}</p>
                        <DefaultTooltip
                            readMore={true}
                            title={t('tool-tip.edit-device')}
                            content={
                                <div className='flex flex-col gap-3'>
                                    <p>{t('tool-tip.this-view-allows-the-editing-of-the-device-details')}.</p>
                                </div>
                            }>
                            <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                        </DefaultTooltip>
                    </div>
                    <GrayIconButton onClick={handleCloseDialog}>
                        <CloseIcon />
                    </GrayIconButton>
                </div>
            </DialogTitle>
            <DialogContent>
                <div className='grid grid-cols-2 gap-x-10'>
                    <div className='col-span-1'>
                        <CustomInput
                            required={true}
                            title={t('common.device-code')}
                            inputType="text"
                            name="deviceCode"
                            // isError={isEmailError}
                            // isSuccess={isEmailSuccess}
                            placeholder={t('common.device-code')}
                            message={validationMessages.deviceCode}
                            handleInputChange={handleInputChange}
                            value={formData.deviceCode}
                        />
                        <div className='flex !text-[#EAB308] items-center pt-2 gap-1'>
                            <InfoIconYellow16pxSvg />
                            <p>{t('common.this-code-should-be-unique-for-a-divice')}.</p>
                        </div>
                    </div>
                    <div className='col-span-1'>
                        <CustomInput
                            required={true}
                            title={t('common.description')}
                            inputType="text"
                            name="groupDescription"
                            id="editGroupDescription"
                            // isError={isEmailError}
                            // isSuccess={isEmailSuccess}
                            placeholder={t('common.description')}
                            message={validationMessages.description}
                            handleInputChange={handleInputChange}
                            value={formData.description} />
                    </div>
                </div>
                <div className='configurations pt-5'>
                    <p className='font-semibold uppercase'>{t('common.configuration')}</p>
                    <div className='pt-4'>
                        <label className="block text-sm">
                            {t('common.biometric-authentication')}
                            <span className='text-[#DC2626] ml-1'>*</span>
                        </label>
                        <div className='mt-2 mb-3'>
                            <FormControl className='w-full'>
                                <CustomSelect
                                    name="biometricAuthentication"
                                    value={formData.biometricAuthentication}
                                    onChange={handleInputChange}
                                    options={biometricAuthenticationData}
                                    defaultValue="identification"
                                />
                            </FormControl>
                        </div>
                    </div>
                    {formData.biometricAuthentication === "identification" && <FormControlLabel
                        control={
                            <Checkbox checked={formData.verifyOnDuplicateMatching}
                                onChange={handleCheckedChange}
                                name="verifyOnDuplicateMatching"
                                disableRipple
                            />
                        }
                        label="Verify on duplicate matching"
                    />}
                    {formData.biometricAuthentication === "verification" &&
                        <div>
                            <p>{t('common.verification-order')}</p>
                            <FormControl component="fieldset" >
                                <RadioGroup
                                    row
                                    aria-label="verificationOrder"
                                    name="verificationOrder"
                                    value={formData.verificationOrder}
                                    onChange={handleInputChange}
                                >
                                    <FormControlLabel value="idFirst" control={<Radio size='small' />} label={t('common.id-first')} />
                                    <FormControlLabel value="biometricFirst" control={<Radio size='small' />} label={t('common.biometric-first')} />
                                </RadioGroup>
                            </FormControl>
                        </div>
                    }
                    <div>
                        <p>{t('common.events')}</p>
                        <FormGroup row={true}>
                            <FormControlLabel
                                control={
                                    <Checkbox checked={formData.events.checkIn}
                                        onChange={handleCheckedChange}
                                        name="checkIn"
                                        disableRipple
                                    />
                                }
                                label={t('common.check-in')}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox checked={formData.events.checkOut}
                                        onChange={handleCheckedChange}
                                        name="checkOut"
                                        disableRipple
                                    />
                                }
                                label={t('common.check-out')}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox checked={formData.events.access}
                                        onChange={handleCheckedChange}
                                        name="access"
                                        disableRipple
                                    />
                                }
                                label={t('common.access')}
                            />
                        </FormGroup>
                    </div>
                    <div>
                        <div className='flex gap-1 items-center'>
                            <p>{t('common.external-executables')}</p>
                            <DefaultTooltip
                                readMore={true}
                                title={t('tool-tip.external-executables')}
                                content={
                                    <div className='flex flex-col gap-3'>
                                        <p>{t('tool-tip.system-can-be-configured-to-run-external-programs')}.</p>
                                    </div>
                                }>
                                <QuestionIcon16pxSvg className="fill-[#BDBDBD]" />
                            </DefaultTooltip>
                        </div>
                        <div className='pt-1'>
                            <ControlpanelPrimaryButton onClick={handleAddExternalExecutables}>
                                <AddIcon />
                                <p>{t('button.add-new')}</p>
                            </ControlpanelPrimaryButton>
                        </div>
                        <div className='flex flex-col-reverse'>
                            {formData.externalExecutables.map((exe, index) => (
                                <ExternalExecutetable key={index}
                                    onRemove={() => handleRemoveExternalExecutables(index)} />
                            ))}
                        </div>
                    </div>
                    <div className='mt-3'>
                        <StyledLinkBlue>{t('common.reset-configuration')}</StyledLinkBlue>
                    </div>
                    <div className='mt-3'>
                        <PrimaryDivider />
                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                <div className='flex gap-2 px-4 py-4'>
                    <ControlpanelSecondaryButton onClick={props.handleClose}>
                        <CloseIcon />
                        <p>{t('button.close')}</p>
                    </ControlpanelSecondaryButton>
                    <ControlpanelPrimaryButton onClick={props.handleClose}>
                        <SaveIcon14pxSvg />
                        <p>{t('button.save')}</p>
                    </ControlpanelPrimaryButton>
                </div>
            </DialogActions>
        </CustomDialog>
    )
}

export default EditDeviceDialog